import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiUrl, mockAuthHeaders } from '../lib/api';

export type AlertType =
  | 'date_slip'
  | 'stuck_task'
  | 'capacity_overload'
  | 'skill_gap'
  | 'unconfirmed_order'
  | 'data_integrity'
  | 'time_entry_conflict'
  | 'pin_priority_conflict'
  | 'partial_staged'
  | 'explosion_review_required'
  | 'memory_pressure'
  | 'scheduling_skipped';

export type AlertStatus = 'active' | 'acknowledged' | 'resolved';

export interface Alert {
  id: string;
  alertType: AlertType;
  sourceEntityType: string;
  sourceEntityId: string;
  status: AlertStatus;
  payload: Record<string, unknown> | null;
  createdAt: string;
  acknowledgedAt: string | null;
  acknowledgedBy: { id: string; name: string } | null;
  resolvedAt: string | null;
  resolvedBy: { id: string; name: string } | null;
  resolution: string | null;
  relatedEntity?: Record<string, unknown> | null;
}

export interface AlertCounts {
  byType: Record<AlertType, number>;
  byStatus: Record<AlertStatus, number>;
  totalActive: number;
}

export interface AlertFilters {
  alertType?: AlertType;
  status?: AlertStatus;
  sourceEntityType?: string;
}

async function fetchAlerts(filters: AlertFilters = {}): Promise<Alert[]> {
  const params = new URLSearchParams();
  if (filters.alertType) params.append('alertType', filters.alertType);
  if (filters.status) params.append('status', filters.status);
  if (filters.sourceEntityType) params.append('sourceEntityType', filters.sourceEntityType);

  const response = await fetch(apiUrl(`/api/v1/alerts?${params.toString()}`), {
    headers: {
      'Content-Type': 'application/json',
      ...mockAuthHeaders(),
    },
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch alerts');
  }

  const data = await response.json();
  return data.data || data;
}

async function fetchAlertCounts(): Promise<AlertCounts> {
  const response = await fetch(apiUrl('/api/v1/alerts/counts'), {
    headers: {
      'Content-Type': 'application/json',
      ...mockAuthHeaders(),
    },
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch alert counts');
  }

  const data = await response.json();
  return data.data || data;
}

async function fetchAlertDetail(id: string): Promise<Alert> {
  const response = await fetch(apiUrl(`/api/v1/alerts/${id}`), {
    headers: {
      'Content-Type': 'application/json',
      ...mockAuthHeaders(),
    },
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch alert details');
  }

  const data = await response.json();
  return data.data || data;
}

async function acknowledgeAlert(id: string): Promise<void> {
  const response = await fetch(apiUrl(`/api/v1/alerts/${id}`), {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...mockAuthHeaders(),
    },
    credentials: 'include',
    body: JSON.stringify({ status: 'acknowledged' }),
  });

  if (!response.ok) {
    throw new Error('Failed to acknowledge alert');
  }
}

async function resolveAlert(id: string, resolution?: string): Promise<void> {
  const response = await fetch(apiUrl(`/api/v1/alerts/${id}`), {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...mockAuthHeaders(),
    },
    credentials: 'include',
    body: JSON.stringify({ status: 'resolved', resolution }),
  });

  if (!response.ok) {
    throw new Error('Failed to resolve alert');
  }
}

async function dismissAlert(id: string): Promise<void> {
  const response = await fetch(apiUrl(`/api/v1/alerts/${id}`), {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      ...mockAuthHeaders(),
    },
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to dismiss alert');
  }
}

async function bulkAlertAction(
  action: 'acknowledge' | 'dismiss' | 'resolve',
  alertIds: string[],
  resolution?: string
): Promise<{ affected: number }> {
  const response = await fetch(apiUrl('/api/v1/alerts/bulk'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...mockAuthHeaders(),
    },
    credentials: 'include',
    body: JSON.stringify({ action, alertIds, resolution }),
  });

  if (!response.ok) {
    throw new Error(`Failed to ${action} alerts`);
  }

  const data = await response.json();
  return data.data || data;
}

export function useAlerts(filters: AlertFilters = {}) {
  return useQuery({
    queryKey: ['alerts', filters],
    queryFn: () => fetchAlerts(filters),
    staleTime: 30000,
    refetchInterval: 60000, // Poll every minute for new alerts
  });
}

export function useAlertCounts() {
  return useQuery({
    queryKey: ['alertCounts'],
    queryFn: fetchAlertCounts,
    staleTime: 30000,
    refetchInterval: 60000,
  });
}

export function useAlertDetail(id: string | null) {
  return useQuery({
    queryKey: ['alert', id],
    queryFn: () => fetchAlertDetail(id!),
    enabled: !!id,
  });
}

export function useAcknowledgeAlert() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: acknowledgeAlert,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      queryClient.invalidateQueries({ queryKey: ['alertCounts'] });
    },
  });
}

export function useResolveAlert() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, resolution }: { id: string; resolution?: string }) =>
      resolveAlert(id, resolution),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      queryClient.invalidateQueries({ queryKey: ['alertCounts'] });
    },
  });
}

export function useDismissAlert() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: dismissAlert,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      queryClient.invalidateQueries({ queryKey: ['alertCounts'] });
    },
  });
}

export function useBulkAlertAction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      action,
      alertIds,
      resolution,
    }: {
      action: 'acknowledge' | 'dismiss' | 'resolve';
      alertIds: string[];
      resolution?: string;
    }) => bulkAlertAction(action, alertIds, resolution),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      queryClient.invalidateQueries({ queryKey: ['alertCounts'] });
    },
  });
}
